import { FASTElement, Updates, attr, customElement, html, observable } from '@microsoft/fast-element';
import { uniqueId } from '@microsoft/fast-web-utilities';

import type { ViewTemplate } from '@microsoft/fast-element';

import template from './template.js';
import styles from './styles.js';
import { throttle } from '../../utilities/throttle.js';
import { debounce } from '../../utilities/debouce.js';

@customElement({
	name: 'site-carousel',
	template,
	styles
})
export class Carousel extends FASTElement {
	@attr({ attribute: 'view-mode' })
	public viewMode: 'mobile' | null = null;
	protected viewModeChanged(_oldValue: string | null, newValue: string | null): void {
		if (newValue === 'mobile') {
			this.items?.forEach((element) => {
				element.removeAttribute('inert');
			});
		}
	}

	@observable
	public items!: HTMLElement[];
	protected itemsChanged(
		_oldValue: HTMLElement[] | undefined,
		newValue: HTMLElement[]
	) :void {
		if (newValue.length) {
			newValue.map((element, index) => {
				
				if (!element.hasAttribute('id')) {
					const id = uniqueId('item-');
					element.setAttribute('id', id);
				}
				
				if (index === 0) {
					this.activeId ??= element.id;
				}

				element.setAttribute('role', 'tabpanel');
				element.setAttribute('aria-roledescription', 'slide');
				element.setAttribute('aria-label', `${index + 1} of ${this.items.length}`);

				element.setAttribute('inert', '');
			});
		}
	}

	@observable
	public scrollContainer!: HTMLElement;

	@observable
	public paginationContainer!: HTMLElement;

	@attr({ attribute: 'active-id' })
	public activeId: string | null = null;
	protected activeIdChanged(
		oldValue: string | null,
		newValue: string
	): void {
		Updates.enqueue(() => {
			if ((oldValue !== null && oldValue !== undefined) || newValue !== undefined) {
				const previousItem = this.items?.find((element) => element.id === oldValue);
				const nextItem = this.items?.find((element) => element.id === newValue);

				previousItem?.toggleAttribute('inert');

				this.scrollContainer.scrollTo({ behavior: 'smooth', left: nextItem?.offsetLeft ?? 0 })
				nextItem?.removeAttribute('inert');

				this.fadeStart = newValue === this.items?.[0]?.id ? 0 : 1;
				this.fadeEnd = newValue === this.items?.[this.items.length - 1]?.id ? 0 : 1;
			}
		});
	}

	@observable
	public fadeStart: number = 0;

	@observable
	public fadeEnd: number = 0;

	public previous(): void {
		const activeItem = this.items.find((element) => element.id === this.activeId);
		const previousItem = activeItem?.previousElementSibling;

		if (previousItem) {
			this.activeId = previousItem.id;
		}
	
	}

	public next(): void {
		const activeItem = this.items.find((element) => element.id === this.activeId);
		const nextItem = activeItem?.nextElementSibling;

		if (nextItem) {
			this.activeId = nextItem.id;
		}
	}

	public createPagination(): ViewTemplate[] {
		const buttons: ViewTemplate[] = [];

		if (this.items.length) {

			this.items.forEach((element, index) => {
				const template = html`
					<button
						role="tab"
						aria-label="slide ${index + 1}"
						aria-selected=${() => this.activeId === element.id ? true : false}
						@click=${() => this.activeId = element.id}
					></button>
				`;

				buttons.push(template);
			});
		}

		return buttons;
	}

	@debounce(100)
	private handleScroll(event: Event): void {
		this.updatePagination(event);
	}

	@throttle()
	private handleScrollEnd(event: Event): void {
		this.updatePagination(event);
	}

	public updatePagination(event: Event): void {
		Updates.enqueue(() => {
			const containerRect = this.scrollContainer.getBoundingClientRect();
			const inViewItem: HTMLElement = this.items.find(element => {
				const rect = element.getBoundingClientRect();
	
				return (
					rect.left >= containerRect.left &&
					rect.right <= containerRect.right
				);
			})!;
	
			this.activeId = inViewItem?.id ? inViewItem.id : this.activeId;
		});
	}

	public connectedCallback(): void {
		super.connectedCallback();

		Updates.enqueue(() => {
			if ('onscrollend' in document.documentElement) {
				this.scrollContainer.addEventListener('scrollend', this.handleScrollEnd.bind(this), { passive: true });
			} else {
				this.scrollContainer.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
			}
		});
	}

	public disconnectedCallback(): void {
		if ('onscrollend' in document.documentElement) {
			this.scrollContainer.removeEventListener('scrollend', this.handleScrollEnd.bind(this));
		} else {
			this.scrollContainer.removeEventListener('scroll', this.handleScroll.bind(this));
		}

		super.disconnectedCallback();
	}
}